/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Menu, Transition } from '@headlessui/react';
import isEmpty from 'lodash.isempty';
import Media from 'react-media';

import IconSvg from 'components/Core/IconSvg';
import Cta from 'components/Core/Cta';

import trackAction from 'utils/tracking';
import { ICON_TO_ACTIVITY_BG } from 'constants/ActivityTheme';

import GLOBAL_MEDIA_QUERIES from 'utils/facility';

import { CheckboxInput as CheckboxInputElem, RadioInput as RadioInputElem } from './Filter.styles';

const Filter = ({
  filterSetsGeneric,
  filterSetsTheme,
  themes,
  activeTheme,
  onFilterChange,
  onThemeChange,
  resultCount,
  initialFilterData,
  resetFilters,
  isLoading,
  activeFiltersGeneric,
  activeFiltersTheme,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const themeFilterSet = {
    name: 'Thema',
    slug: 'thema-custom',
    filters: [{ id: null, icon: 'all', name: 'Alles' }, ...themes],
  };

  // Dirty fix to prevent purging these classes because they are dynamically generated
  // TODO: See if we can figure out a smarter way to do this
  // eslint-disable-next-line
  const usedClasses = `bg-activity-relax-400 bg-activity-sport-400 bg-activity-learn-400 bg-activity-outing-400
  lg:bg-activity-relax-400 lg:bg-activity-sport-400 lg:bg-activity-learn-400 lg:bg-activity-outing-400
  hover:bg-activity-relax hover:bg-activity-sport hover:bg-activity-learn hover:bg-activity-outing
  focus:bg-activity-relax focus:bg-activity-sport focus:bg-activity-learn focus:bg-activity-outing
  bg-activity-relax bg-activity-sport bg-activity-learn bg-activity-outing
  border-activity-relax border-activity-sport border-activity-learn border-activity-outing
  text-activity-relax text-activity-sport text-activity-learn text-activity-outing`;

  const listFilters = ({ name, slug, icon, id }, filterSet) => {
    if (icon) {
      const themeColor = ICON_TO_ACTIVITY_BG[icon];
      const borderClass = `border-${themeColor}`;
      const bgClass = `bg-${themeColor}-400`;
      return (
        <label
          key={`filter-${filterSet}-${icon}`}
          htmlFor={`filter-${filterSet}-${icon}`}
          className="cursor-pointer flex-1/2 max-w-1/2 lg:flex-full lg:max-w-full px-2 lg:px-0 flex items-center my-3 focus:outline-none lg:mt-4"
        >
          <RadioInputElem
            id={`filter-${filterSet}-${icon}`}
            type="radio"
            name="theme-filter"
            className="hidden"
            onChange={(e) => {
              onThemeChange(id);
              trackAction('activity-filter', name, e.target.checked ? 'select' : 'deselect');
            }}
            value={icon}
            checked={activeTheme ? id === activeTheme.id : id === null}
          />
          <div className="hidden lg:block relative w-6 h-6 border-2 rounded-full border-primary">
            <div className="absolute indicator w-3 h-3 m-1 bg-primary rounded-full hidden" />
          </div>
          <div
            className={`border-2 lg:border-0 ${
              activeTheme && icon === activeTheme.icon ? `${borderClass}` : 'border-primary'
            } ${
              activeTheme && icon === activeTheme.icon ? `lg:bg-transparent ${bgClass}` : ''
            } rounded-100 p-2 lg:py-0 w-full lg:w-auto text-center lg:text-left`}
          >
            {name}
          </div>
        </label>
      );
    }

    const category = initialFilterData.find((item) => item.filterSet === filterSet);

    return (
      <label
        key={`filter-${filterSet}-${slug}`}
        className="flex flex-full items-center cursor-pointer my-3 focus:outline-none lg:mt-4"
        htmlFor={`filter-${filterSet}-${slug}`}
      >
        <div className="w-6 h-6 mr-2 border-2 rounded border-primary">
          <CheckboxInputElem
            id={`filter-${filterSet}-${slug}`}
            type="checkbox"
            name={`checkbox-${filterSet}-${slug}`}
            className="hidden"
            onChange={(e) => {
              onFilterChange(filterSet, slug);
              trackAction('activity-filter', name, e.target.checked ? 'select' : 'deselect');
            }}
            value={slug}
            checked={category ? category.filters.find((element) => element === slug) : false}
          />
          <IconSvg width={15} height={15} icon="check" className="text-primary ml-1 inline-block transform scale-0" />
        </div>
        <nobr>{name}</nobr>
      </label>
    );
  };

  const listFilterSets = ({ name, slug, theme, filters }) => (
    <div
      key={`filterset-item-${slug}`}
      className="relative leading-5 border-b-2 border-basic-200 last:border-b-0 mt-2 lg:mt-0 lg:border-b-0 lg:mr-4 xxl:mr-6"
    >
      <Menu>
        {({ open }) => {
          let themeColor = 'lg:bg-basic-200';
          let textColor = 'text-basic-700';

          if (theme && activeTheme) {
            const colorCode = ICON_TO_ACTIVITY_BG[theme.icon];
            themeColor = `lg:bg-${colorCode}-400`;
            textColor = `text-${ICON_TO_ACTIVITY_BG[activeTheme.icon]} lg:text-basic-700`;
          }

          return (
            <>
              <Menu.Button
                onClick={() => {
                  trackAction('activity-filter', name, open ? 'open' : 'close');
                }}
                className={`${textColor} group flex my-3 items-center w-full justify-between h6 lg:my-0 xl:h5 focus:outline-none`}
              >
                <div
                  className={`${
                    open ? 'border-primary font-bold' : 'border-transparent'
                  } lg:border-b-2 lg:py-2 group-hover:border-primary group-focus:border-primary transition-color ease-in-out duration-150`}
                >
                  {name}
                </div>
                <IconSvg icon="arrow-down" className={`${open && 'u-rotate-180'} text-primary h-3 w-3 ml-2`} />
              </Menu.Button>
              <Transition
                unmount={false}
                show={open}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Menu.Items
                  static
                  className="lg:absolute transform lg:px-2 lg:min-w-180 lg:max-w-md lg:left-1/4 lg:ml-0 lg:mt-3 lg:-translate-x-1/4 focus:outline-none"
                >
                  <div className="rounded-lg lg:border-b-2 border-basic-300">
                    <div className="rounded-lg overflow-hidden">
                      <div
                        className={`pb-2 ${themeColor} lg:px-6 lg:pt-2 ${
                          slug === 'thema-custom' && 'flex flex-wrap -mx-2 lg:mx-0 lg:block'
                        }`}
                      >
                        {filters.map((filter) => listFilters(filter, slug))}
                      </div>
                    </div>
                  </div>
                </Menu.Items>
              </Transition>
            </>
          );
        }}
      </Menu>
    </div>
  );

  const getFilterPill = ({ name, slug, filterSet }) => {
    let themeColor = 'bg-primary-300 border-primary hover:bg-primary focus:bg-primary';
    let textColor = 'text-primary';

    if (filterSet.theme) {
      const colorCode = ICON_TO_ACTIVITY_BG[filterSet.theme.icon];
      themeColor = `bg-${colorCode}-400 border-${colorCode} hover:bg-${colorCode} focus:bg-${colorCode}`;
      textColor = `text-${colorCode}`;
    }

    return (
      <button
        key={`filter-button-${slug}`}
        type="button"
        onClick={() => onFilterChange(filterSet.slug, slug)}
        className={`mt-2 ${themeColor} hover:text-white group border-2 focus:outline-none transition-colors ease-out duration-150 rounded-100 px-4 py-1 mr-2 flex justify-between items-center`}
      >
        <div className="capitalize mr-6">{name}</div>
        <IconSvg
          className={`${textColor} group-hover:text-white focus:text-white`}
          icon="close"
          width={19}
          height={19}
        />
      </button>
    );
  };

  return (
    <>
      <div>
        <div
          className={`fixed z-50 inset-0 lg:relative lg:z-40 lg:block ${isLoading && 'pointer-events-none'} ${
            !isOpen ? 'hidden' : 'overflow-y-auto'
          }`}
        >
          <div className="min-h-screen bg-white lg:min-h-0 lg:bg-transparent">
            <div className="pt-5 pb-20 lg:px-3 lg:py-0">
              <div className="flex items-center px-5 lg:px-0 justify-between lg:hidden">
                <div className="flex items-end">
                  <h4 className="leading-snug">Filters</h4>
                  <button
                    type="button"
                    className="text-primary font-semibold ml-3 h5 leading-snug"
                    onClick={() => {
                      setIsOpen(false);
                      resetFilters();
                      document.body.classList.remove('overflow-hidden');
                      trackAction('activity-filter', 'reset', 'click');
                    }}
                  >
                    Reset
                  </button>
                </div>
                <div className="-mr-2">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => {
                      setIsOpen(false);
                      document.body.classList.remove('overflow-hidden');
                      trackAction('activity-filter', 'close', 'click');
                    }}
                    className="disabled:opacity-50 inline-flex items-center justify-center p-2 rounded-md focus:outline-none transition duration-150 ease-in-out"
                  >
                    <IconSvg icon="close" className="text-primary h-6 w-6" />
                  </button>
                </div>
              </div>
              <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ sm: true }}>
                {(matches) =>
                  matches.xs || matches.sm || matches.md ? (
                    <>
                      <div className="mt-6 px-5">{listFilterSets(themeFilterSet)}</div>
                      {activeTheme && !isEmpty(filterSetsTheme) && (
                        <div
                          className={`bg-${
                            ICON_TO_ACTIVITY_BG[activeTheme.icon]
                          }-400 rounded-b-base border-b-2 border-t-2 border-basic-200 py-2 px-5`}
                        >
                          {filterSetsTheme.map(listFilterSets)}
                        </div>
                      )}
                      <div className="mt-6 px-5">{filterSetsGeneric.map(listFilterSets)}</div>
                    </>
                  ) : (
                    <>
                      <div className="lg:bg-basic-200 lg:z-10 lg:relative lg:rounded-b-base lg:p-8 mt-6 lg:mt-0 lg:flex lg:flex-wrap">
                        {listFilterSets(themeFilterSet)}

                        {filterSetsGeneric.map(listFilterSets)}
                        <div className="leading-5 text-basic-700 mt-2 ml-auto h6 xl:h5">
                          Sorteren op: <span className="font-bold">A-Z</span>
                        </div>
                      </div>
                      {activeTheme && !isEmpty(filterSetsTheme) && (
                        <div
                          className={`bg-${
                            ICON_TO_ACTIVITY_BG[activeTheme.icon]
                          }-400 rounded-b-base lg:p-8 lg:-mt-3 lg:flex lg:flex-wrap`}
                        >
                          {filterSetsTheme.map(listFilterSets)}
                        </div>
                      )}
                    </>
                  )
                }
              </Media>
              <div className="inset-x-15 fixed bottom-15 lg:hidden">
                <Cta
                  onClick={() => {
                    setIsOpen(false);
                    document.body.classList.remove('overflow-hidden');
                    trackAction('activity-filter', 'view-results', 'submit');
                  }}
                  showIcon={false}
                  className="w-full disabled:opacity-50"
                  disabled={isLoading}
                >
                  Bekijk {isLoading ? '...' : resultCount} resultaten
                </Cta>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`hidden lg:flex lg:flex-wrap lg:mt-2 lg:px-3 ${isLoading && 'opacity-50 pointer-events-none'}`}>
        {activeTheme && (
          <button
            key={`filter-button-${activeTheme.icon}`}
            type="button"
            onClick={() => onThemeChange(null)}
            className="mt-2 bg-primary-300 hover:bg-primary hover:text-white border-2 border-primary hover:border-primary-600 focus:border-primary-600 transition-colors ease-out duration-150 group rounded-100 px-4 py-1 mr-2 flex justify-between items-center focus:outline-none"
          >
            <div className="capitalize mr-6">{activeTheme.name}</div>
            <IconSvg
              className="text-primary group-hover:text-white group-focus:text-white"
              icon="close"
              width={19}
              height={19}
            />
          </button>
        )}
        {activeFiltersTheme.map(getFilterPill)}
        {activeFiltersGeneric.map(getFilterPill)}
      </div>
      <div className="inset-x-15 fixed z-20 bottom-15 lg:hidden">
        <Cta
          onClick={() => {
            setIsOpen(true);
            document.body.classList.add('overflow-hidden');
            trackAction('suggested-activities', 'filter', 'click');
          }}
          showIcon={false}
          className="w-full"
        >
          Filter
        </Cta>
      </div>
    </>
  );
};

Filter.propTypes = {
  filterSetsGeneric: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  filterSetsTheme: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  activeFiltersGeneric: PropTypes.arrayOf(Object),
  activeFiltersTheme: PropTypes.arrayOf(Object),
  themes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  activeTheme: PropTypes.shape({
    id: PropTypes.string,
    icon: PropTypes.string,
    name: PropTypes.string,
  }),
  onFilterChange: PropTypes.func,
  onThemeChange: PropTypes.func,
  resultCount: PropTypes.number.isRequired,
  initialFilterData: PropTypes.arrayOf(Object),
  resetFilters: PropTypes.func,
  isLoading: PropTypes.bool,
};

Filter.defaultProps = {
  onFilterChange: () => {},
  onThemeChange: () => {},
  initialFilterData: [],
  resetFilters: () => {},
  isLoading: false,
  activeTheme: null,
  activeFiltersGeneric: [],
  activeFiltersTheme: [],
};

export default Filter;
